import React, { useState, useMemo, useEffect } from 'react';
import './styles/DividerEditor.css';

// 样式类型定义
const STYLE_CATEGORIES = {
  basic: {
    name: '基础线条',
    styles: [
      { value: 'solid', label: '实线', icon: '─' },
      { value: 'dashed', label: '虚线', icon: '┅' },
      { value: 'dotted', label: '点线', icon: '┄' },
      { value: 'double', label: '双线', icon: '═' },
    ]
  },
  decoration: {
    name: '装饰线条',
    styles: [
      { value: 'line-dot', label: '线+圆点', icon: '●' },
      { value: 'line-square', label: '线+方形', icon: '■' },
      { value: 'line-diamond', label: '线+菱形', icon: '◆' },
      { value: 'line-triangle', label: '线+三角', icon: '▲' },
    ]
  },
  special: {
    name: '特殊效果',
    styles: [
      { value: 'wave', label: '波浪线', icon: '〰' },
      { value: 'gradient', label: '渐变线', icon: '▬' },
    ]
  }
};

export default function DividerEditor({ data, onUpdate }) {
  // 使用 useMemo 让 cfg 响应 data 的变化
  const cfg = useMemo(() => ({ ...(data || {}) }), [data]);
  
  // 根据当前样式确定分类，响应 cfg.style 的变化
  const getCategoryByStyle = (style) => {
    if (style === 'line-dot' || style === 'line-square' || style === 'line-diamond' || style === 'line-triangle') {
      return 'decoration';
    }
    if (style === 'wave' || style === 'gradient') {
      return 'special';
    }
    return 'basic';
  };

  const [activeCategory, setActiveCategory] = useState(() => getCategoryByStyle(cfg.style));

  // 当样式变化时，自动切换到对应的分类
  useEffect(() => {
    const newCategory = getCategoryByStyle(cfg.style);
    setActiveCategory(prevCategory => {
      // 只有当分类真正改变时才更新，避免不必要的重渲染
      return newCategory !== prevCategory ? newCategory : prevCategory;
    });
  }, [cfg.style]);

  // 修复 update 函数，使用最新的 data 而不是缓存的 cfg
  const update = (patch) => {
    const currentData = { ...(data || {}) };
    onUpdate({ ...currentData, ...patch });
  };

  // 确定当前样式是否支持装饰元素，响应 cfg.style 的变化
  const supportsDecoration = useMemo(() => {
    return ['line-dot', 'line-square', 'line-diamond', 'line-triangle'].includes(cfg.style);
  }, [cfg.style]);
  
  // 确定是否支持渐变，响应 cfg.style 的变化
  const supportsGradient = useMemo(() => {
    return cfg.style === 'gradient';
  }, [cfg.style]);

  // 计算预览样式
  const previewStyle = useMemo(() => {
    const style = {
      justifyContent: cfg.align === 'center' ? 'center' : (cfg.align === 'left' ? 'flex-start' : 'flex-end'),
      marginTop: cfg.marginTop || 12,
      marginBottom: cfg.marginBottom || 12,
    };
    return style;
  }, [cfg.align, cfg.marginTop, cfg.marginBottom]);

  const renderPreview = () => {
    const decorationSize = cfg.decorationSize || cfg.dotSize || 8;
    const decorationColor = cfg.decorationColor || cfg.dotColor || cfg.color || '#D4BFA0';

    // 渲染装饰元素
    const renderDecoration = () => {
      if (!supportsDecoration) return null;
      
      const decorationStyle = {
        width: decorationSize,
        height: decorationSize,
        backgroundColor: decorationColor,
        flexShrink: 0,
      };

      switch (cfg.style) {
        case 'line-dot':
          decorationStyle.borderRadius = decorationSize;
          break;
        case 'line-square':
          decorationStyle.borderRadius = 0;
          break;
        case 'line-diamond':
          decorationStyle.clipPath = 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)';
          break;
        case 'line-triangle':
          decorationStyle.clipPath = 'polygon(50% 0%, 0% 100%, 100% 100%)';
          break;
        default:
          decorationStyle.borderRadius = decorationSize;
      }

      return <span className="de-decoration" style={decorationStyle} />;
    };

    // 渲染线条（与 DividerContent.js 保持一致）
    const renderLine = () => {
      const lineStyle = {
        width: `${cfg.lengthPercent || 88}%`,
        flexShrink: 0,
      };

      // 根据样式类型设置不同的线条样式
      switch (cfg.style) {
        case 'solid':
        case 'line-dot':
        case 'line-square':
        case 'line-diamond':
        case 'line-triangle':
          // 实线
          lineStyle.height = cfg.thickness || 4;
          lineStyle.backgroundColor = cfg.color || '#D4BFA0';
          lineStyle.borderRadius = (cfg.thickness || 4) / 2;
          break;

        case 'dashed':
          // 虚线
          lineStyle.height = 0;
          lineStyle.borderTop = `${cfg.thickness || 4}px dashed ${cfg.color || '#D4BFA0'}`;
          lineStyle.backgroundColor = 'transparent';
          lineStyle.display = 'inline-block';
          break;

        case 'dotted':
          // 点线
          lineStyle.height = 0;
          lineStyle.borderTop = `${cfg.thickness || 4}px dotted ${cfg.color || '#D4BFA0'}`;
          lineStyle.backgroundColor = 'transparent';
          lineStyle.display = 'inline-block';
          break;

        case 'double':
          // 双线
          lineStyle.height = 0;
          const doubleThickness = Math.max(1, Math.floor((cfg.thickness || 4) / 3));
          lineStyle.borderTop = `${doubleThickness}px double ${cfg.color || '#D4BFA0'}`;
          lineStyle.backgroundColor = 'transparent';
          lineStyle.display = 'inline-block';
          break;

        case 'gradient':
          // 渐变线
          lineStyle.height = cfg.thickness || 4;
          const gradientFrom = cfg.gradientFrom || cfg.color || '#D4BFA0';
          const gradientTo = cfg.gradientTo || cfg.color || '#E5D4B0';
          lineStyle.background = `linear-gradient(90deg, ${gradientFrom}, ${gradientTo})`;
          lineStyle.borderRadius = (cfg.thickness || 4) / 2;
          break;

        case 'wave':
          // 波浪线 - 使用SVG pattern重复
          const waveHeight = (cfg.thickness || 4) * 2;
          const waveWidth = (cfg.thickness || 4) * 4;
          const patternId = `preview-wave-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
          return (
            <svg 
              className="de-wave"
              width={`${cfg.lengthPercent || 88}%`}
              height={waveHeight}
              style={{ flexShrink: 0, display: 'block' }}
              preserveAspectRatio="none"
            >
              <defs>
                <pattern id={patternId} x="0" y="0" width={waveWidth} height={waveHeight} patternUnits="userSpaceOnUse">
                  <path
                    d={`M 0 ${waveHeight / 2} Q ${waveWidth / 4} 0, ${waveWidth / 2} ${waveHeight / 2} T ${waveWidth} ${waveHeight / 2}`}
                    stroke={cfg.color || '#D4BFA0'}
                    strokeWidth={cfg.thickness || 4}
                    fill="none"
                  />
                </pattern>
              </defs>
              <rect width="100%" height={waveHeight} fill={`url(#${patternId})`} />
            </svg>
          );

        default:
          lineStyle.height = cfg.thickness || 4;
          lineStyle.backgroundColor = cfg.color || '#D4BFA0';
          lineStyle.borderRadius = (cfg.thickness || 4) / 2;
      }

      return <span className="de-line" style={lineStyle} />;
    };

    const line = renderLine();
    const decoration = renderDecoration();

    // 根据对齐方式组合元素
    let elements = [];
    if (cfg.align === 'center' && supportsDecoration) {
      elements = [decoration, line, decoration];
    } else if (cfg.align === 'left' && supportsDecoration) {
      elements = [decoration, line];
    } else if (cfg.align === 'right' && supportsDecoration) {
      elements = [line, decoration];
    } else {
      elements = [line];
    }

    return (
      <div className="de-preview-line" style={previewStyle}>
        {elements}
      </div>
    );
  };

  const handleStyleChange = (styleValue) => {
    update({ style: styleValue });
    // 如果切换到不支持装饰的样式，清除装饰相关配置
    if (!['line-dot', 'line-square', 'line-diamond', 'line-triangle'].includes(styleValue)) {
      update({ decorationColor: undefined, decorationSize: undefined });
    }
  };

  return (
    <div className="divider-editor">
      <h3 className="de-title">分隔线设置</h3>
      
      {/* Tab切换 */}
      <div className="de-tabs">
        {Object.entries(STYLE_CATEGORIES).map(([key, category]) => (
          <button
            key={key}
            className={`de-tab ${activeCategory === key ? 'active' : ''}`}
            onClick={() => setActiveCategory(key)}
          >
            {category.name}
          </button>
        ))}
      </div>

      {/* 预览区域 */}
      <div className="de-preview-section">
        <div className="de-preview-label">预览</div>
        <div className="de-preview-box">
          {cfg.showTop !== false && (
            <div className="de-preview-item">
              <span className="de-preview-tag">顶部</span>
              {renderPreview()}
            </div>
          )}
          {cfg.showBottom && (
            <div className="de-preview-item">
              <span className="de-preview-tag">底部</span>
              {renderPreview()}
            </div>
          )}
          {!cfg.showTop && !cfg.showBottom && (
            <div className="de-preview-item">
              {renderPreview()}
            </div>
          )}
        </div>
      </div>

      {/* 基础设置 */}
      <div className="de-section">
        <div className="de-section-title">基础设置</div>
        <div className="de-grid">
          <label className="de-label">
            <span>对齐方式</span>
            <select
              className="de-select"
              value={cfg.align || 'right'}
              onChange={(e) => update({ align: e.target.value })}
            >
              <option value="left">靠左</option>
              <option value="center">居中</option>
              <option value="right">靠右</option>
            </select>
          </label>

          <label className="de-label">
            <span>线条颜色</span>
            <div className="de-color-wrapper">
              <input
                type="color"
                className="de-color-input"
                value={cfg.color || '#D4BFA0'}
                onChange={(e) => update({ color: e.target.value })}
              />
              <span className="de-color-value">{cfg.color || '#D4BFA0'}</span>
            </div>
          </label>
        </div>
      </div>

      {/* 渐变设置（仅渐变样式） */}
      {supportsGradient && (
        <div className="de-section">
          <div className="de-section-title">渐变设置</div>
          <div className="de-grid">
            <label className="de-label">
              <span>起始颜色</span>
              <div className="de-color-wrapper">
                <input
                  type="color"
                  className="de-color-input"
                  value={cfg.gradientFrom || cfg.color || '#D4BFA0'}
                  onChange={(e) => update({ gradientFrom: e.target.value })}
                />
                <span className="de-color-value">{cfg.gradientFrom || cfg.color || '#D4BFA0'}</span>
              </div>
            </label>

            <label className="de-label">
              <span>结束颜色</span>
              <div className="de-color-wrapper">
                <input
                  type="color"
                  className="de-color-input"
                  value={cfg.gradientTo || cfg.color || '#E5D4B0'}
                  onChange={(e) => update({ gradientTo: e.target.value })}
                />
                <span className="de-color-value">{cfg.gradientTo || cfg.color || '#E5D4B0'}</span>
              </div>
            </label>
          </div>
        </div>
      )}

      {/* 尺寸设置 */}
      <div className="de-section">
        <div className="de-section-title">尺寸设置</div>
        <div className="de-grid">
          <label className="de-label">
            <span>线条粗细</span>
            <div className="de-slider-wrapper">
              <input
                type="range"
                className="de-slider"
                min="1"
                max="12"
                value={cfg.thickness || 4}
                onChange={(e) => update({ thickness: Number(e.target.value) })}
              />
              <span className="de-slider-value">{cfg.thickness || 4}px</span>
            </div>
          </label>

          <label className="de-label">
            <span>线条长度</span>
            <div className="de-slider-wrapper">
              <input
                type="range"
                className="de-slider"
                min="10"
                max="100"
                value={cfg.lengthPercent || 88}
                onChange={(e) => update({ lengthPercent: Number(e.target.value) })}
              />
              <span className="de-slider-value">{cfg.lengthPercent || 88}%</span>
            </div>
          </label>
        </div>
      </div>

      {/* 装饰设置（仅装饰样式） */}
      {supportsDecoration && (
        <div className="de-section">
          <div className="de-section-title">装饰设置</div>
          <div className="de-grid">
            <label className="de-label">
              <span>装饰颜色</span>
              <div className="de-color-wrapper">
                <input
                  type="color"
                  className="de-color-input"
                  value={cfg.decorationColor || cfg.dotColor || cfg.color || '#D4BFA0'}
                  onChange={(e) => update({ decorationColor: e.target.value, dotColor: e.target.value })}
                />
                <span className="de-color-value">{cfg.decorationColor || cfg.dotColor || cfg.color || '#D4BFA0'}</span>
              </div>
            </label>

            <label className="de-label">
              <span>装饰大小</span>
              <div className="de-slider-wrapper">
                <input
                  type="range"
                  className="de-slider"
                  min="4"
                  max="24"
                  value={cfg.decorationSize || cfg.dotSize || 8}
                  onChange={(e) => update({ decorationSize: Number(e.target.value), dotSize: Number(e.target.value) })}
                />
                <span className="de-slider-value">{cfg.decorationSize || cfg.dotSize || 8}px</span>
              </div>
            </label>
          </div>
        </div>
      )}

      {/* 位置设置 */}
      <div className="de-section">
        <div className="de-section-title">位置设置</div>
        <div className="de-grid">
          <label className="de-label">
            <span>显示位置</span>
            <div className="de-checkbox-group">
              <label className="de-checkbox-label">
                <input
                  type="checkbox"
                  checked={cfg.showTop !== false}
                  onChange={(e) => update({ showTop: e.target.checked })}
                />
                <span>顶部</span>
              </label>
              <label className="de-checkbox-label">
                <input
                  type="checkbox"
                  checked={!!cfg.showBottom}
                  onChange={(e) => update({ showBottom: e.target.checked })}
                />
                <span>底部</span>
              </label>
            </div>
          </label>

          <label className="de-label">
            <span>上边距</span>
            <div className="de-slider-wrapper">
              <input
                type="range"
                className="de-slider"
                min="0"
                max="48"
                value={cfg.marginTop || 12}
                onChange={(e) => update({ marginTop: Number(e.target.value) })}
              />
              <span className="de-slider-value">{cfg.marginTop || 12}px</span>
            </div>
          </label>

          <label className="de-label">
            <span>下边距</span>
            <div className="de-slider-wrapper">
              <input
                type="range"
                className="de-slider"
                min="0"
                max="48"
                value={cfg.marginBottom || 12}
                onChange={(e) => update({ marginBottom: Number(e.target.value) })}
              />
              <span className="de-slider-value">{cfg.marginBottom || 12}px</span>
            </div>
          </label>
        </div>
      </div>
    </div>
  );
}
